import { Navigate, useLocation } from 'react-router-dom';
import { TenantProvider } from '../contexts/TenantContext';
import { UserContext } from '../contexts/UserContext';
import { useAuthUser } from '../hooks/useQueries';
import { PORTAL_ADMIN_URL } from '../constants';
import type { ApiError } from '../utils/api-helper';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const { data: user, isLoading, isError, error } = useAuthUser();

  // Nur bei initialem Laden (kein gecachter User) Spinner zeigen
  if (isLoading && !user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  // Redirect nur bei echter Auth-Ablauf (401). Ein 403 (Berechtigungsfehler) darf keinen Logout auslösen.
  const isAuthError = isError || (error as ApiError | null)?.status === 401;
  if (isAuthError || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // super_admin verwaltet TireVault nicht mehr selbst (siehe Memory
  // project_superadmin_portal_migration) — raus aus TireVault, rein ins Portal.
  if (user.role === 'super_admin') {
    window.location.href = PORTAL_ADMIN_URL;
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  // MULTI-TENANT: Wrap children with UserContext and TenantProvider
  return (
    <UserContext.Provider value={{ user }}>
      <TenantProvider>
        {children}
      </TenantProvider>
    </UserContext.Provider>
  );
}